import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Edit, Trash2, Plus, Upload, Download } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDownloads = () => {
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingDownload, setEditingDownload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    try {
      const response = await axios.get(`${API_URL}/downloads`);
      setDownloads(response.data);
    } catch (error) {
      toast.error('Failed to fetch downloads');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (downloadData) => {
    try {
      if (downloadData.id) {
        // Update existing
        await axios.put(
          `${API_URL}/admin/downloads/${downloadData.id}`,
          downloadData,
          { headers: getAuthHeader() }
        );
        toast.success('Download updated successfully');
      } else {
        // Create new
        await axios.post(
          `${API_URL}/admin/downloads`,
          downloadData,
          { headers: getAuthHeader() }
        );
        toast.success('Download added successfully');
      }
      setEditingDownload(null);
      fetchDownloads();
    } catch (error) {
      toast.error('Failed to save download');
    }
  };

  const handleDelete = async (downloadId) => {
    if (!window.confirm('Are you sure you want to delete this download?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/downloads/${downloadId}`, {
        headers: getAuthHeader()
      });
      toast.success('Download deleted successfully');
      fetchDownloads();
    } catch (error) {
      toast.error('Failed to delete download');
    }
  };

  const handleFileUpload = async (e, downloadId) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/admin/upload-file`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      // Update the download with the file URL
      const downloadToUpdate = downloads.find(d => d.id === downloadId);
      if (downloadToUpdate) {
        const updated = { ...downloadToUpdate, url: response.data.url, size: response.data.size };
        await axios.put(
          `${API_URL}/admin/downloads/${downloadId}`,
          updated,
          { headers: getAuthHeader() }
        );
        toast.success('File uploaded successfully');
        fetchDownloads();
      }
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <AdminLayout title="Downloads"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Manage Downloads">
      <div className="space-y-6">
        <Button
          onClick={() => setEditingDownload({ 
            title: '', 
            description: '', 
            fileType: 'PDF',
            size: '',
            url: '#'
          })}
          className="bg-sage-700 hover:bg-sage-800 text-white"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Download
        </Button>

        <div className="grid md:grid-cols-2 gap-6">
          {downloads.map((download) => (
            <Card key={download.id} className="border-0 shadow-lg">
              <CardContent className="p-6 space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{download.title}</h3>
                    <p className="text-sm text-gray-600 mt-1">{download.fileType} • {download.size}</p>
                  </div>
                  <span className="bg-sage-100 text-sage-800 px-3 py-1 rounded-full text-xs font-medium">
                    {download.fileType}
                  </span>
                </div>
                <p className="text-gray-700">{download.description}</p>
                
                <div className="space-y-2">
                  <label className="block text-xs font-medium text-gray-700">
                    Upload/Replace File
                  </label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => handleFileUpload(e, download.id)}
                    disabled={uploading}
                    className="w-full text-sm px-3 py-2 rounded-lg border-2 border-sage-200 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-sage-100 file:text-sage-700 hover:file:bg-sage-200 file:cursor-pointer"
                  />
                </div>

                <div className="flex space-x-2 pt-4">
                  <Button
                    onClick={() => setEditingDownload(download)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-sage-300 text-sage-700"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Details
                  </Button>
                  <Button
                    onClick={() => handleDelete(download.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-red-300 text-red-700"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {editingDownload && (
        <DownloadEditModal
          download={editingDownload}
          onSave={handleSave}
          onClose={() => setEditingDownload(null)}
        />
      )}
    </AdminLayout>
  );
};

const DownloadEditModal = ({ download, onSave, onClose }) => {
  const [formData, setFormData] = useState(download);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardContent className="p-8">
          <h3 className="text-2xl font-serif mb-6">{download.id ? 'Edit Download' : 'New Download'}</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                placeholder="e.g., Explant Preparation Checklist"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                rows={3}
                placeholder="Brief description of the resource"
                className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none resize-none"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">File Type</label>
                <select
                  value={formData.fileType}
                  onChange={(e) => setFormData({ ...formData, fileType: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                >
                  <option value="PDF">PDF</option>
                  <option value="DOC">DOC</option>
                  <option value="DOCX">DOCX</option>
                  <option value="TXT">TXT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">File Size (optional)</label>
                <input
                  type="text"
                  value={formData.size}
                  onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                  placeholder="e.g., 2.4 MB"
                  className="w-full px-4 py-2 rounded-lg border-2 border-sage-200 focus:border-sage-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex space-x-4 pt-4">
              <Button type="submit" className="flex-1 bg-sage-700 hover:bg-sage-800 text-white">
                <Download className="mr-2 h-4 w-4" />
                Save Download
              </Button>
              <Button type="button" onClick={onClose} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDownloads;
